import {
  ApolloLink,
  HttpLink,
  type HttpOptions,
  type RequestHandler,
  type ServerParseError,
  split,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { getMainDefinition } from "@apollo/client/utilities";
import { parseSerializedErrorMessage } from "@atoms/atom-types";
import { default as ApolloLinkTimeout } from "apollo-link-timeout";

/**
 * Error callbacks for link chain
 */
export interface LinkErrorCallbacks {
  onGraphQLError?: (message: string) => void;
  onNetworkError?: (error: Error | CloseEvent) => void;
  onWsGraphQLError?: (message: string) => void;
  onWsNetworkError?: (error: Error | CloseEvent) => void;
}

/**
 * Configuration for creating the HTTP link chain
 */
export interface HttpLinkChainConfig {
  httpOptions: HttpOptions;
  timeout: { timeout: number; statusCode?: number };
  retry: RetryLink.Options;
  getAuthToken: () => Promise<string | null>;
  callbacks?: LinkErrorCallbacks;
  logger?: {
    error: (message: string) => void;
  };
}

/**
 * Creates an authentication link that adds the auth token to headers.
 *
 * @param getAuthToken - Function to get the current auth token
 * @returns Apollo link that adds auth headers
 */
export function createAuthLink(
  getAuthToken: () => Promise<string | null>,
): ApolloLink {
  return setContext(async (_request, previousContext) => {
    const token = await getAuthToken();
    return {
      headers: {
        ...previousContext.headers,
        authorization: token,
      },
    };
  });
}

/**
 * Creates a timeout link for HTTP requests.
 *
 * @param timeout - Timeout in milliseconds
 * @param statusCode - Optional status code to use for timeout errors
 * @returns Apollo link that enforces timeout
 */
export function createTimeoutLink(
  timeout: number,
  statusCode?: number,
): ApolloLink {
  return new ApolloLinkTimeout(timeout, statusCode);
}

/**
 * Creates a retry link for failed requests.
 *
 * @param options - Retry configuration options
 * @returns Apollo link that handles retries
 */
export function createRetryLink(options: RetryLink.Options): ApolloLink {
  return new RetryLink(options);
}

/**
 * Creates an error handling link for HTTP requests.
 *
 * @param callbacks - Error callbacks
 * @param logger - Optional logger for error messages
 * @returns Apollo link that handles errors
 */
export function createHttpErrorLink(
  callbacks?: LinkErrorCallbacks,
  logger?: { error: (message: string) => void },
): ApolloLink {
  return new ErrorLink(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      for (const { message, locations, path } of graphQLErrors) {
        const locs = locations
          ?.map((l) => `line: ${l.line} col: ${l.column}`)
          .join(",");
        logger?.error(
          `graphql error: Message: ${message}, Location: ${locs ?? "unknown"}, Path: ${path}`,
        );
        callbacks?.onGraphQLError?.(message);
      }
    }
    if (networkError) {
      // Check if error response is JSON and fix the message if needed
      if ((networkError as ServerParseError).bodyText) {
        const parsedError = parseSerializedErrorMessage(
          (networkError as ServerParseError).bodyText,
        );
        if (!parsedError) {
          networkError.message = (networkError as ServerParseError).bodyText;
        }
      }
      logger?.error(`network error: ${networkError}`);
      callbacks?.onNetworkError?.(networkError);
    }
  });
}

/**
 * Creates an error handling link for WebSocket subscriptions.
 *
 * @param callbacks - Error callbacks
 * @param logger - Optional logger for error messages
 * @returns Apollo link that handles subscription errors
 */
export function createSubscriptionErrorLink(
  callbacks?: LinkErrorCallbacks,
  logger?: { error: (message: string) => void },
): ApolloLink {
  return new ErrorLink(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      for (const { message, locations, path } of graphQLErrors) {
        const locs = locations
          ?.map((l) => `line: ${l.line} col: ${l.column}`)
          .join(",");

        // Skip unauthorized errors (usually handled elsewhere)
        if (message === "Unauthorized") {
          return;
        }

        logger?.error(
          `subscription graphql error: Message: ${message}, Location: ${locs ?? "unknown"}, Path: ${path}`,
        );
        callbacks?.onWsGraphQLError?.(message);
      }
    }
    if (networkError) {
      if ((networkError as ServerParseError).bodyText) {
        try {
          JSON.parse((networkError as ServerParseError).bodyText);
        } catch (_e) {
          networkError.message = (networkError as ServerParseError).bodyText;
        }
      }
      if (networkError instanceof Error) {
        logger?.error(`subscription network error: ${networkError.message}`);
      }
      if (networkError instanceof CloseEvent) {
        logger?.error(
          `subscription network error: socket closed with event ${networkError.code} ${networkError.reason || ""}`,
        );
      }
      callbacks?.onWsNetworkError?.(networkError);
    }
  });
}

/**
 * Creates a link that adds operation name to the request for debugging.
 *
 * @param baseUri - The base URI for GraphQL requests
 * @returns Apollo link that adds operation metadata
 */
export function createNamedOperationLink(baseUri: string): ApolloLink {
  return new ApolloLink((operation, forward) => {
    const isGraphqlNames =
      typeof localStorage !== "undefined" &&
      typeof localStorage.getItem === "function" &&
      localStorage.getItem("graphqlNames");
    // biome-ignore lint/suspicious/noExplicitAny: legacy code
    operation.setContext(({ headers }: any) => ({
      uri: isGraphqlNames ? `${baseUri}?${operation.operationName}` : baseUri,
      headers: {
        ...headers,
        "x-apollo-operation-name": operation.operationName,
      },
    }));
    return forward(operation);
  });
}

/**
 * Splits traffic between subscription and query/mutation links.
 *
 * @param subscriptionLink - Link chain for subscriptions (WebSocket)
 * @param queryMutationLink - Link chain for queries/mutations (HTTP)
 * @returns Combined split link
 */
export function createSplitLink(
  subscriptionLink: ApolloLink,
  queryMutationLink: ApolloLink,
): ApolloLink {
  return split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    subscriptionLink,
    queryMutationLink,
  );
}

/**
 * Composes multiple links into a single link chain.
 *
 * @param links - Array of links to compose
 * @returns Combined link chain
 */
export function composeLinks(
  links: (ApolloLink | RequestHandler | undefined)[],
): ApolloLink {
  return ApolloLink.from(
    links.filter(Boolean) as (ApolloLink | RequestHandler)[],
  );
}

/**
 * Creates the complete HTTP link chain for queries and mutations.
 *
 * @param config - Configuration for the link chain
 * @param additionalLinks - Additional links to include (e.g., rehydrateDatesLink)
 * @returns Composed HTTP link chain
 */
export function createHttpLinkChain(
  config: HttpLinkChainConfig,
  additionalLinks: ApolloLink[] = [],
): ApolloLink {
  const httpLink = new HttpLink(config.httpOptions);
  const authLink = createAuthLink(config.getAuthToken);
  const timeoutLink = createTimeoutLink(
    config.timeout.timeout,
    config.timeout.statusCode,
  );
  const retryLink = createRetryLink(config.retry);
  const errorLink = createHttpErrorLink(config.callbacks, config.logger);
  const namedLink = createNamedOperationLink(config.httpOptions.uri as string);

  return composeLinks([
    timeoutLink,
    retryLink,
    errorLink,
    ...additionalLinks,
    namedLink,
    authLink,
    httpLink,
  ]);
}
